import { useContext, useEffect, memo, useMemo, FC, PropsWithChildren } from "react";

import {
  UserRoles,
  UserFormPermissions,
  UserGeneralPermissions,
  Emptyable,
  isOnboardingAllowedForCountry,
} from "@simplyk/common";
import { useRouter } from "next/router";

import { ENVIRONMENTS_BOOLEANS, isReviewApp } from "../../../common/constants/env";
import { useCurrentUserContext } from "../../../common/contexts/CurrentUserContext";
import { redirectToLogout } from "../../../common/helpers/auth";
import { useCardVolume } from "../../../common/hooks/useCardVolume";
import { useGtm } from "../../../common/hooks/useGtm";
import { PageLoadingContext } from "../../../common/providers/PageLoadingProvider";

import { AvailableSoonModal } from "./AvailableSoonModal";

import { setObjectInLocalStorage } from "@/common/helpers/localStorage";
import { useLogRouteChangePageVisits, RouteType } from "@/common/hooks/amplitude/useLogRouteChangePageVisits";
import { useRedirect } from "@/common/hooks/useRedirect";
import { useRedirectionStatus } from "@/common/hooks/useRedirectionStatus";

interface OrganizationRouteProps extends PropsWithChildren {
  formPermissions?: Emptyable<UserFormPermissions[]>;
  generalPermissions?: Emptyable<UserGeneralPermissions[]>;
}

const OrganizationRouteToMemoized: FC<OrganizationRouteProps> = ({ children, formPermissions, generalPermissions }) => {
  const router = useRouter();
  const { pushGtmFirstPage } = useGtm();
  useLogRouteChangePageVisits({ path: router.pathname, routeType: RouteType.OrganizationRoute });
  const { setRedirectionLoading } = useContext(PageLoadingContext);
  const { redirectToDefault } = useRedirect();
  const { isAuthenticated, currentUser, connectedAsAdministrator, organization } = useCurrentUserContext();
  const { isLoading, shouldBeInDonorDashboard, shouldBeLoggedOut } = useRedirectionStatus();

  const { data: cardVolumeData, isLoading: cardVolumeLoading } = useCardVolume({
    enabled: !shouldBeInDonorDashboard,
  });

  const hasGeneralPermission = useMemo(() => {
    return (
      !!generalPermissions &&
      generalPermissions.some((permission) => currentUser?.newPermissions?.generalPermissions?.includes(permission))
    );
  }, [generalPermissions, currentUser?.newPermissions?.generalPermissions]);

  const hasFormPermission = useMemo(() => {
    return (
      !!formPermissions &&
      formPermissions.some((permission) => currentUser?.newPermissions?.formPermissions?.includes(permission))
    );
  }, [formPermissions, currentUser?.newPermissions?.formPermissions]);

  const shouldRedirectForPermissions = useMemo(() => {
    if (isLoading || !isAuthenticated) {
      return false;
    }
    return (generalPermissions && !hasGeneralPermission) || (formPermissions && !hasFormPermission);
  }, [isLoading, isAuthenticated, generalPermissions, hasGeneralPermission, formPermissions, hasFormPermission]);

  const isRedirectionLoading = Boolean(
    isLoading || shouldBeLoggedOut || shouldBeInDonorDashboard || shouldRedirectForPermissions
  );

  useEffect(() => {
    setRedirectionLoading(isRedirectionLoading);
  }, [isRedirectionLoading, setRedirectionLoading]);

  useEffect(() => {
    if (currentUser?.email && !cardVolumeLoading && !connectedAsAdministrator) {
      pushGtmFirstPage({ email: currentUser?.email, paymentCount: cardVolumeData?.object?.count || 0 });
      // Used by marketing team to know if user has already an admin Zeffy account.
      setObjectInLocalStorage?.("zeffyAdminUser", {
        userId: currentUser?.id,
        paymentCount: cardVolumeData?.object?.count || 0,
      });
    }
  }, [
    connectedAsAdministrator,
    currentUser?.email,
    cardVolumeData?.object?.count,
    pushGtmFirstPage,
    cardVolumeLoading,
    currentUser?.id,
  ]);

  useEffect(() => {
    if (shouldBeLoggedOut) {
      const currentPath = router.asPath;
      if (isReviewApp) {
        void router.replace(`/review-app/login?redirectionUrl=${encodeURIComponent(currentPath)}`);
      } else if (process.env.NODE_ENV !== "development") {
        redirectToLogout({ redirectionUrl: currentPath, connectedAsAdministrator });
      }
    }
  }, [shouldBeLoggedOut, router, connectedAsAdministrator]);

  useEffect(() => {
    if (
      !shouldBeLoggedOut &&
      (shouldBeInDonorDashboard || currentUser?.role === UserRoles.Donor || shouldRedirectForPermissions)
    ) {
      redirectToDefault();
    }
  }, [shouldBeLoggedOut, shouldBeInDonorDashboard, currentUser?.role, shouldRedirectForPermissions, redirectToDefault]);

  const isOnboardingAllowed = useMemo(() => {
    return !organization?.country || isOnboardingAllowedForCountry(organization?.country, ENVIRONMENTS_BOOLEANS);
  }, [organization?.country]);

  const handleCloseCountryEligibilityModal = () => {
    redirectToLogout({ redirectionUrl: undefined, connectedAsAdministrator: false });
  };

  return (
    <>
      <AvailableSoonModal isOpen={!isOnboardingAllowed} close={handleCloseCountryEligibilityModal} />
      {children}
    </>
  );
};

export const OrganizationRoute = memo(OrganizationRouteToMemoized);
